import { BlankModal, Flex, ModalBody, ModalFooter, ModalHeader, VStack } from '@cad-arc';
import React from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { useFullscreenModalControls } from '../../modals/context/modal-context-provider';
import {
    DispatchAreaOption,
    ManageDispatchAreasResource,
    SendDispatchAreasUserOption,
} from '@mark43/cad-api';
import { IntegratedFormProvider } from '../../forms/integrated-form-provider';
import { FormBody } from '../../forms/integrated-form-components/form-body';
import { FormCancelButton } from '../../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../../forms/integrated-form-components/form-save-button';
import { FormSelect } from '../../forms/integrated-form-controls/form-select';

export type SendDispatchAreasModalType = typeof SendDispatchAreasModal;

type SendDispatchAreasForm = {
    dispatchAreas: DispatchAreaOption[];
    user: SendDispatchAreasUserOption;
};

type FormFieldOptions = {
    dispatchAreas: DispatchAreaOption[];
    users: SendDispatchAreasUserOption[];
};

function SendDispatchAreasForm(props: {
    form: UseFormReturn<SendDispatchAreasForm>;
    formFieldOptions: FormFieldOptions;
}): React.ReactElement {
    return (
        <VStack minHeight="350px">
            <FormSelect
                name="dispatchAreas"
                label="D-Area(s)"
                isMultiple={true}
                options={props.formFieldOptions.dispatchAreas}
                control={props.form.control}
                rules={{
                    required: 'Required',
                }}
                selectProps={{
                    maxMenuHeight: 250,
                }}
            />
            <FormSelect
                label="Recipient"
                options={props.formFieldOptions.users}
                name="user"
                control={props.form.control}
                rules={{
                    required: 'Required',
                }}
                selectProps={{
                    maxMenuHeight: 250,
                }}
            />
        </VStack>
    );
}

export function SendDispatchAreasModal(): React.ReactElement {
    const modalControls = useFullscreenModalControls();
    const close = React.useCallback(() => {
        modalControls.close();
    }, [modalControls]);

    const apiClient = useApiClient();

    const [formFieldOptions, setFormFieldOptions] = React.useState<FormFieldOptions>({
        dispatchAreas: [],
        users: [],
    });

    const query = useQuery(
        ['dispatch-area-state'],
        () => {
            return apiClient
                .request<ManageDispatchAreasResource.GetSendDispatchAreasView>({
                    method: 'GET',
                    url: 'manage_dispatch_areas/send',
                })
                .then((data) => {
                    if (data.data.data) {
                        return data.data.data;
                    }
                });
        },
        {
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            staleTime: Infinity,
            cacheTime: 0,
        }
    );

    const { data: sendDispatchAreaView } = query;

    const form = useForm<SendDispatchAreasForm>({
        defaultValues: {
            dispatchAreas: undefined,
            user: undefined,
        },
        mode: 'onBlur',
    });

    const mutation = useMutation(
        (form: SendDispatchAreasForm) => {
            return apiClient.request<ManageDispatchAreasResource.SendDispatchAreas>({
                method: 'POST',
                url: 'manage_dispatch_areas/send',
                data: {
                    dispatchAreaOptions: form.dispatchAreas,
                    sendDispatchAreasUserOption: form.user,
                },
            });
        },
        {
            onSuccess() {
                close();
            },
        }
    );

    React.useEffect(() => {
        if (sendDispatchAreaView) {
            setFormFieldOptions({
                dispatchAreas: sendDispatchAreaView.dispatchAreaOptions,
                users: sendDispatchAreaView.sendDispatchAreasUserOptions,
            });
            form.reset({
                dispatchAreas: undefined,
                user: undefined,
            });
        }
    }, [form, sendDispatchAreaView]);

    const onSubmit = React.useCallback(
        (props: SendDispatchAreasForm): void => {
            mutation.mutate(props);
        },
        [mutation]
    );

    return (
        <IntegratedFormProvider query={query} mutation={mutation} form={form} onSubmit={onSubmit}>
            <BlankModal size="sm" isOpen={true} onClose={close}>
                <ModalHeader>Send D-Area</ModalHeader>
                <ModalBody>
                    <FormBody>
                        <SendDispatchAreasForm form={form} formFieldOptions={formFieldOptions} />
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <Flex gap="2">
                        <FormCancelButton onClick={close} />
                        <FormSaveButton />
                    </Flex>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
}
